/**
 * Instagram Configuration Page (tenant)
 * The Meta app credentials are platform-managed: a super-admin sets them on
 * the tenant's Instagram tab (saas-front TenantInstagramConfig.tsx), exactly
 * as WhatsApp's are, and the tenant API no longer accepts them. What stays
 * here is what only the account owner can do: the Instagram Login consent
 * screen (connect/reconnect/disconnect), plus ice breakers and the persistent
 * menu. A super-admin can additionally hide this whole tab via tenantUiHidden.
 *
 * Design intent (rewritten 2026-09-28): the previous version leaned on a
 * bordered "card with a small icon tile" pattern for every section, which
 * reads as a generic admin-panel template rather than a considered page.
 * This version uses one flat column, generous whitespace as the primary
 * separator (borders only between genuinely distinct zones, never around
 * every box), a single clear type scale, and colour used only where it
 * carries meaning (the connected badge, the Instagram mark, destructive
 * actions) rather than decoratively.
 */

import React, { useEffect, useState, type ReactNode } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Avatar,
  IconButton,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  LinkOff,
  Add,
  DeleteOutline,
  SyncRounded,
  CheckCircleRounded,
  OpenInNewRounded,
  InfoOutlined,
  WarningAmberRounded,
} from '@mui/icons-material';
import { useInstagramConfig } from '@/hooks/useInstagramConfig';
import adminApi, { InstagramIceBreaker, InstagramPersistentMenuItem } from '@/services/admin.api';
import InstagramLogo from '@/components/common/InstagramLogo';
import InstagramAgentSelector from '@/components/admin/InstagramAgentSelector';

const MAX_ICE_BREAKERS = 4;
const MAX_PERSISTENT_MENU_ITEMS = 5;
/** Instagram's own brand gradient (Meta brand resources) — used only on the
 *  Connect button, so it reads as Instagram's action rather than a generic
 *  indigo "primary button" borrowed from the rest of the app. */
const IG_GRADIENT = 'linear-gradient(90deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)';

const INK = '#0f172a';
const MUTED = '#64748b';
const FAINT = '#94a3b8';
const BORDER = '#eef0f3';
const ACCENT = '#4f46e5';

/* ─────────────────────────────── building blocks ─────────────────────────── */

/** A page section: a plain heading + one-line purpose, then content — no
 *  bordered card, no icon tile. Sections are separated by a hairline rule and
 *  vertical space, not by boxing each one individually. */
function Section({
  title,
  subtitle,
  action,
  children,
  first,
}: {
  title: string;
  subtitle: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  first?: boolean;
}) {
  return (
    <Box sx={{ pt: first ? 0 : 4, mt: first ? 0 : 4, borderTop: first ? 'none' : `1px solid ${BORDER}` }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, color: INK, fontSize: '1.0625rem' }}>{title}</Typography>
          <Typography variant="body2" sx={{ color: MUTED, mt: 0.375, lineHeight: 1.55, maxWidth: 560 }}>
            {subtitle}
          </Typography>
        </Box>
        {action}
      </Box>
      {children}
    </Box>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <Box>
      <Typography sx={{ fontWeight: 700, color: INK, fontSize: '1.05rem', lineHeight: 1.2 }}>{value}</Typography>
      <Typography variant="caption" sx={{ color: FAINT, fontWeight: 500 }}>
        {label}
      </Typography>
    </Box>
  );
}

/** One editable row — a plain hairline-separated row, not a bordered card
 *  per item (a list of boxes-within-a-box is what read as templated). */
function EditorRow({ onRemove, children }: { onRemove: () => void; children: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        py: 1.5,
        borderBottom: `1px solid ${BORDER}`,
        '&:first-of-type': { pt: 0 },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.25, flex: 1, minWidth: 0 }}>
        {children}
      </Box>
      <IconButton size="small" onClick={onRemove} sx={{ color: FAINT, mt: 0.5, '&:hover': { color: '#dc2626' } }}>
        <DeleteOutline fontSize="small" />
      </IconButton>
    </Box>
  );
}

function EmptyEditor({ text }: { text: string }) {
  return (
    <Typography variant="body2" sx={{ color: FAINT, py: 2 }}>
      {text}
    </Typography>
  );
}

/** Footer: add on the left, save on the right. Save only lights up when there
 *  is something unsaved, so "did that save?" is never a guess. */
function EditorFooter({
  addLabel,
  canAdd,
  onAdd,
  dirty,
  saving,
  onSave,
}: {
  addLabel: string;
  canAdd: boolean;
  onAdd: () => void;
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
}) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
      <Button size="small" startIcon={<Add sx={{ fontSize: '18px !important' }} />} disabled={!canAdd} onClick={onAdd} sx={{ fontWeight: 600, color: ACCENT }}>
        {addLabel}
      </Button>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {!dirty && !saving && (
          <Typography variant="caption" sx={{ color: FAINT }}>
            Saved
          </Typography>
        )}
        <Button
          variant="contained"
          size="small"
          disableElevation
          onClick={onSave}
          disabled={saving || !dirty}
          sx={{ fontWeight: 600, borderRadius: 1.5, bgcolor: ACCENT, '&:hover': { bgcolor: '#4338ca' }, px: 2 }}
        >
          {saving ? <CircularProgress size={16} color="inherit" /> : 'Save'}
        </Button>
      </Box>
    </Box>
  );
}

const sameList = (a: unknown[], b: unknown[]) => JSON.stringify(a) === JSON.stringify(b);

/* ───────────────────────── ice breakers + persistent menu ────────────────── */

/**
 * Ice breakers + persistent menu (Phase 2.6). Both edit a local draft and only
 * push to Meta on explicit Save. Loaded together (one round-trip), rendered as
 * two sections. `saved*` holds the last server state, which is what makes the
 * dirty/"Saved" indicator honest.
 */
function EngagementSettings({ connected }: { connected: boolean }) {
  const [iceBreakers, setIceBreakers] = useState<InstagramIceBreaker[]>([]);
  const [savedIceBreakers, setSavedIceBreakers] = useState<InstagramIceBreaker[]>([]);
  const [menu, setMenu] = useState<InstagramPersistentMenuItem[]>([]);
  const [savedMenu, setSavedMenu] = useState<InstagramPersistentMenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingIceBreakers, setSavingIceBreakers] = useState(false);
  const [savingMenu, setSavingMenu] = useState(false);
  const [iceBreakersError, setIceBreakersError] = useState('');
  const [menuError, setMenuError] = useState('');

  useEffect(() => {
    if (!connected) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([adminApi.getInstagramIceBreakers(), adminApi.getInstagramPersistentMenu()])
      .then(([ib, pm]) => {
        if (cancelled) return;
        setIceBreakers(ib);
        setSavedIceBreakers(ib);
        setMenu(pm);
        setSavedMenu(pm);
      })
      .catch(() => {
        /* leave both lists empty — the save action will surface any real error */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [connected]);

  if (!connected) return null;
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  const handleSaveIceBreakers = async () => {
    setSavingIceBreakers(true);
    setIceBreakersError('');
    try {
      const saved = await adminApi.saveInstagramIceBreakers(
        iceBreakers.filter((b) => b.question.trim() && b.payload.trim())
      );
      setIceBreakers(saved);
      setSavedIceBreakers(saved);
    } catch (err: any) {
      setIceBreakersError(err?.response?.data?.error?.message || err?.message || 'Failed to save ice breakers');
    } finally {
      setSavingIceBreakers(false);
    }
  };

  const handleSaveMenu = async () => {
    setSavingMenu(true);
    setMenuError('');
    try {
      const saved = await adminApi.saveInstagramPersistentMenu(menu.filter((m) => m.title.trim()));
      setMenu(saved);
      setSavedMenu(saved);
    } catch (err: any) {
      setMenuError(err?.response?.data?.error?.message || err?.message || 'Failed to save the persistent menu');
    } finally {
      setSavingMenu(false);
    }
  };

  const updateIceBreaker = (i: number, patch: Partial<InstagramIceBreaker>) => {
    const next = [...iceBreakers];
    next[i] = { ...next[i]!, ...patch };
    setIceBreakers(next);
  };
  const updateMenuItem = (i: number, patch: Partial<InstagramPersistentMenuItem>) => {
    const next = [...menu];
    next[i] = { ...next[i]!, ...patch };
    setMenu(next);
  };

  return (
    <>
      <Section
        title="Ice breakers"
        subtitle="Suggested questions Instagram shows to someone who opens a chat with your account. Tapping one sends that exact question to your inbox as if the customer typed it — it does not trigger an automatic reply on its own."
        action={
          <Typography variant="caption" sx={{ color: FAINT, fontWeight: 600, whiteSpace: 'nowrap' }}>
            {iceBreakers.length} / {MAX_ICE_BREAKERS}
          </Typography>
        }
      >
        {iceBreakersError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {iceBreakersError}
          </Alert>
        )}
        <Box>
          {iceBreakers.length === 0 && (
            <EmptyEditor text="No ice breakers yet — add a question like “What are your prices?”" />
          )}
          {iceBreakers.map((item, i) => (
            <EditorRow key={i} onRemove={() => setIceBreakers(iceBreakers.filter((_, idx) => idx !== i))}>
              <TextField
                variant="standard"
                label="Question the customer sees"
                fullWidth
                value={item.question}
                onChange={(e) => updateIceBreaker(i, { question: e.target.value })}
              />
              <TextField
                variant="standard"
                label="Tracking tag"
                fullWidth
                value={item.payload}
                onChange={(e) => updateIceBreaker(i, { payload: e.target.value })}
                helperText={
                  i === 0
                    ? 'Not shown to the customer. Lets you tell this tap apart from others later — write a short code like PRICING_TAP.'
                    : ''
                }
              />
            </EditorRow>
          ))}
        </Box>
        <EditorFooter
          addLabel="Add question"
          canAdd={iceBreakers.length < MAX_ICE_BREAKERS}
          onAdd={() => setIceBreakers([...iceBreakers, { question: '', payload: '' }])}
          dirty={!sameList(iceBreakers, savedIceBreakers)}
          saving={savingIceBreakers}
          onSave={handleSaveIceBreakers}
        />
      </Section>

      <Section
        title="Persistent menu"
        subtitle="A menu of shortcuts inside your DM's message box — Instagram's official docs describe it as 'always visible in a person's conversation with your business.' A Reply item sends its title to your inbox as a message; a Link item just opens a page. On the Instagram mobile app, an existing chat needs a swipe-down-to-refresh in the inbox list to pick up a change (that's Meta's own documented behavior, not a bug here)."
        action={
          <Typography variant="caption" sx={{ color: FAINT, fontWeight: 600, whiteSpace: 'nowrap' }}>
            {menu.length} / {MAX_PERSISTENT_MENU_ITEMS}
          </Typography>
        }
      >
        {menuError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {menuError}
          </Alert>
        )}
        <Box>
          {menu.length === 0 && <EmptyEditor text="No menu items yet — add “Talk to an agent” or a link to your website." />}
          {menu.map((item, i) => (
            <EditorRow key={i} onRemove={() => setMenu(menu.filter((_, idx) => idx !== i))}>
              <TextField
                variant="standard"
                label="Title"
                value={item.title}
                onChange={(e) => updateMenuItem(i, { title: e.target.value })}
                sx={{ minWidth: { sm: 160 } }}
              />
              <TextField
                variant="standard"
                select
                label="Type"
                value={item.type}
                onChange={(e) => updateMenuItem(i, { type: e.target.value as 'postback' | 'web_url' })}
                sx={{ minWidth: { sm: 110 } }}
              >
                <MenuItem value="postback">Reply</MenuItem>
                <MenuItem value="web_url">Link</MenuItem>
              </TextField>
              {item.type === 'postback' ? (
                <TextField
                  variant="standard"
                  label="Tracking tag"
                  fullWidth
                  value={item.payload || ''}
                  onChange={(e) => updateMenuItem(i, { payload: e.target.value })}
                />
              ) : (
                <TextField
                  variant="standard"
                  label="URL"
                  fullWidth
                  placeholder="https://"
                  value={item.url || ''}
                  onChange={(e) => updateMenuItem(i, { url: e.target.value })}
                />
              )}
            </EditorRow>
          ))}
        </Box>
        <EditorFooter
          addLabel="Add item"
          canAdd={menu.length < MAX_PERSISTENT_MENU_ITEMS}
          onAdd={() => setMenu([...menu, { title: '', type: 'postback', payload: '' }])}
          dirty={!sameList(menu, savedMenu)}
          saving={savingMenu}
          onSave={handleSaveMenu}
        />
      </Section>
    </>
  );
}

/* ─────────────────────────────── helpers ─────────────────────────────────── */

function formatCount(n?: number | null): string {
  if (n === undefined || n === null) return '—';
  return n.toLocaleString();
}

function formatDate(iso?: string): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? undefined : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** One-time banner from the OAuth callback redirect (?instagram=connected|denied|expired|error). */
function useCallbackBanner() {
  const [banner, setBanner] = useState<{ severity: 'success' | 'warning' | 'error'; text: string } | null>(
    null
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('instagram');
    if (!status) return;

    if (status === 'connected') {
      const username = params.get('username');
      setBanner({
        severity: 'success',
        text: username ? `Connected as @${username}.` : 'Instagram connected.',
      });
    } else if (status === 'denied') {
      setBanner({
        severity: 'warning',
        text: 'Connection cancelled — Instagram access was not granted.',
      });
    } else if (status === 'expired') {
      setBanner({
        severity: 'error',
        text: 'That connection attempt took too long and expired. Please try again.',
      });
    } else if (status === 'error') {
      setBanner({
        severity: 'error',
        text: "Something went wrong connecting your Instagram account. Please try again — if it keeps happening, check that your Instagram account is Professional (Business or Creator) and hasn't lost its Tester access.",
      });
    }

    // Strip the one-time query params so a refresh doesn't re-show the banner.
    params.delete('instagram');
    params.delete('username');
    params.delete('reason');
    const cleanQuery = params.toString();
    window.history.replaceState(
      {},
      '',
      window.location.pathname + (cleanQuery ? `?${cleanQuery}` : '')
    );
  }, []);

  return banner;
}

/* ─────────────────────────────── page ────────────────────────────────────── */

const InstagramConfig: React.FC = () => {
  const { config, loading, connecting, error, success, connectInstagram, disconnectInstagram } =
    useInstagramConfig();
  const callbackBanner = useCallbackBanner();
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  // Guards the "Switch account" action only (see the icon button's own doc
  // comment) — reconnecting the SAME account after an expiry skips this.
  const [confirmSwitch, setConfirmSwitch] = useState(false);

  if (loading && !config) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (config?.tenantUiHidden) {
    return (
      <Box maxWidth={560}>
        <Alert severity="info">Instagram settings are managed by your platform administrator.</Alert>
      </Box>
    );
  }

  const expired = config?.tokenStatus === 'expired';
  const renewsBy = formatDate(config?.tokenExpiresAt);
  // Connecting is possible when setup is complete AND, in platform mode, the
  // server actually has the shared app configured (appConfigComplete is always
  // true in platform mode, so it alone can't catch a server missing the
  // INSTAGRAM_PLATFORM_* env — platformConfigured does).
  const platformNotReady = config?.authMode === 'platform' && config?.platformConfigured === false;
  const canConnect = Boolean(config?.appConfigComplete) && !platformNotReady;

  return (
    <Box>
      {callbackBanner && (
        <Alert severity={callbackBanner.severity} sx={{ mb: 2.5 }}>
          {callbackBanner.text}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2.5 }}>
          {success}
        </Alert>
      )}

      {/* App credentials are platform-managed (super-admin → tenant → Instagram
          tab), never entered here. Until they exist the Connect button below
          stays disabled, so say why rather than leave a dead button. */}
      {config && !canConnect && (
        <Alert severity="info" sx={{ mb: 2.5 }}>
          Instagram isn't set up for your workspace yet. Ask your platform administrator to configure it,
          then connect your account here.
        </Alert>
      )}

      {!config?.isConnected ? (
        /* ── Not connected ─────────────────────────────────────────────── */
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5 }}>
          <InstagramLogo size={44} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, color: INK, fontSize: '1.1rem' }}>
              Connect your Instagram account
            </Typography>
            <Typography variant="body2" sx={{ color: MUTED, mt: 0.5, lineHeight: 1.6, maxWidth: 480 }}>
              Reply to Instagram Direct messages from your inbox, alongside WhatsApp. You'll sign in with
              Instagram and approve access — we never see your password.
            </Typography>
            <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.6 }}>
              {['A Professional (Business or Creator) account', 'Messages from anyone who has DMed you appear in Chat Logs'].map((t) => (
                <Box key={t} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <CheckCircleRounded sx={{ fontSize: 15, color: '#10b981' }} />
                  <Typography variant="body2" sx={{ color: INK }}>
                    {t}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* A requirement worth noticing, not an inline aside — its own row
                with an icon reads as a real prerequisite instead of small print. */}
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.25,
                p: 1.5,
                borderRadius: 2,
                bgcolor: '#fdf2f8',
              }}
            >
              <InfoOutlined sx={{ fontSize: 18, color: '#be185d', mt: '1px', flexShrink: 0 }} />
              <Typography variant="body2" sx={{ color: '#831843', lineHeight: 1.55 }}>
                Requires a Professional (Business or Creator) account — personal accounts aren't
                supported.{' '}
                <Typography
                  component="a"
                  href="https://help.instagram.com/502981923235522"
                  target="_blank"
                  rel="noopener"
                  variant="body2"
                  sx={{ color: '#be185d', fontWeight: 700, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  Convert your account <OpenInNewRounded sx={{ fontSize: 12, verticalAlign: -1 }} />
                </Typography>
              </Typography>
            </Box>

            <Button
              variant="contained"
              disableElevation
              startIcon={connecting ? <CircularProgress size={15} color="inherit" /> : <InstagramLogo variant="mono" size={16} />}
              onClick={connectInstagram}
              disabled={connecting || !canConnect}
              sx={{
                mt: 2.5,
                fontWeight: 600,
                borderRadius: 1.5,
                px: 2.5,
                color: '#fff',
                background: IG_GRADIENT,
                boxShadow: 'none',
                '&:hover': { background: IG_GRADIENT, filter: 'brightness(0.94)', boxShadow: 'none' },
                '&.Mui-disabled': { background: '#e2e8f0', color: FAINT },
              }}
            >
              {connecting ? 'Redirecting…' : 'Connect Instagram'}
            </Button>
          </Box>
        </Box>
      ) : (
        /* ── Connected ─────────────────────────────────────────────────── */
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              {...(config.profilePictureUrl ? { src: config.profilePictureUrl } : {})}
              alt={config.username || 'Instagram account'}
              sx={{ width: 56, height: 56, bgcolor: ACCENT, fontSize: '1.3rem', fontWeight: 700 }}
            >
              {(config.username || '?').charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontWeight: 700, color: INK, fontSize: '1.05rem' }} noWrap>
                  @{config.username}
                </Typography>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.6,
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    color: expired ? '#b45309' : '#059669',
                  }}
                >
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: expired ? '#f59e0b' : '#10b981' }} />
                  {expired ? 'Reconnect needed' : 'Connected'}
                </Box>
              </Box>
              {config.name && (
                <Typography variant="body2" sx={{ color: MUTED }} noWrap>
                  {config.name}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              <Tooltip title={expired ? 'Reconnect' : 'Switch account'}>
                <IconButton
                  // Reconnecting the SAME account (token expired) is routine
                  // maintenance — no data risk, no confirmation needed.
                  // Switching to a DIFFERENT account is the risky one: see
                  // confirmSwitchAccount's own doc comment for why.
                  onClick={expired ? connectInstagram : () => setConfirmSwitch(true)}
                  disabled={connecting}
                  size="small"
                  sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, color: expired ? '#b45309' : MUTED }}
                >
                  {connecting ? <CircularProgress size={16} /> : <SyncRounded fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Disconnect">
                <IconButton
                  onClick={() => setConfirmDisconnect(true)}
                  disabled={loading}
                  size="small"
                  sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, color: MUTED, '&:hover': { color: '#dc2626', borderColor: '#fecaca' } }}
                >
                  <LinkOff fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {expired && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {config.tokenStatusDetail || 'Your Instagram connection needs to be renewed.'} Reconnect to
              resume sending and receiving messages.
            </Alert>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, mt: 2.5 }}>
            <Stat value={formatCount(config.followersCount)} label="Followers" />
            <Stat value={formatCount(config.mediaCount)} label="Posts" />
            <Typography variant="caption" sx={{ color: FAINT, ml: 'auto' }}>
              {expired ? 'Messaging paused' : renewsBy ? `Renews automatically · until ${renewsBy}` : 'Renews automatically'}
            </Typography>
          </Box>
        </Box>
      )}

      {/* AI auto-reply on/off + default-agent pin — only when connected, since
          there's nothing to answer otherwise. Mirrors WhatsAppConfig's selector. */}
      {config?.isConnected && <Box sx={{ mt: 4 }}><InstagramAgentSelector /></Box>}

      <EngagementSettings connected={Boolean(config?.isConnected)} />

      {/*
        Switching accounts: there is only ONE Instagram connection per tenant
        (InstagramConfig is a single row), and conversations are keyed by
        {tenantId, igsid} with no per-account scoping at all — the industry-
        standard reason most inboxes either block a mid-flight account switch
        or, like here, force an explicit acknowledgement of what happens to
        existing history: connecting a different account does NOT migrate,
        archive, or separate the current one's threads. They stay in the same
        inbox, attributed to whichever account is connected NOW, with no way
        to tell which account a given thread actually came from. This is a
        one-account-at-a-time BYO connection, not a multi-account switcher —
        so the honest fix is to say that plainly before Meta's consent screen
        opens, not to silently proceed like a routine reconnect.
      */}
      <Dialog open={confirmSwitch} onClose={() => setConfirmSwitch(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberRounded sx={{ color: '#d97706' }} />
          Switch to a different account?
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            This workspace can only have <strong>one</strong> connected Instagram account at a time.
            {config?.conversationCount ? (
              <>
                {' '}
                You currently have <strong>{config.conversationCount.toLocaleString()}</strong>{' '}
                {config.conversationCount === 1 ? 'conversation' : 'conversations'} from{' '}
                <strong>@{config.username}</strong>.
              </>
            ) : null}
            <Box component="span" sx={{ display: 'block', mt: 1.5 }}>
              Connecting a different account will <strong>not</strong> move, delete, or separate that
              history — it stays in the same inbox, now shown under the newly connected account, with
              no way to tell which account a thread originally came from.
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmSwitch(false)}>Cancel</Button>
          <Button
            variant="contained"
            disableElevation
            onClick={() => {
              setConfirmSwitch(false);
              void connectInstagram();
            }}
            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#4338ca' } }}
          >
            Continue to Instagram
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disconnect is destructive (inbound DMs stop arriving), so it asks first. */}
      <Dialog open={confirmDisconnect} onClose={() => setConfirmDisconnect(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Disconnect @{config?.username}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            New Instagram messages will stop arriving in Chat Logs and you won't be able to reply. Your existing
            conversations stay. You can reconnect any time.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDisconnect(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disableElevation
            onClick={() => {
              setConfirmDisconnect(false);
              void disconnectInstagram();
            }}
          >
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InstagramConfig;
